import { useEffect, useState, type FormEvent } from "react";
import { Activity, Check, Database, RefreshCw, Server, ShieldAlert, UserPlus, X } from "lucide-react";
import { apiRequest, loadRegistrationPolicy, type RegistrationMode } from "../api";

type Overview = Readonly<{ counts: Record<string, number>; databasePool: { idle: number; total: number; waiting: number }; events: Record<string, number>; pushEnabled: boolean; storageBackend: string; timestamp: string; version: string; websocketConnections: number }>;
type SfuStatus = Readonly<{ enabled: boolean; workers: number; rooms: number; participants: number; producers: number; consumers: number }>;
type Report = Readonly<{ category: string; chatName: string | null; comment: string | null; createdAt: string; disclosedContent: string; id: string; messageExists: boolean; messageRetracted: boolean; reportedUsername: string | null; reporterUsername: string }>;

export function AdminModal({ onClose }: Readonly<{ onClose: () => void }>) {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [sfu, setSfu] = useState<SfuStatus | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode | null>(null);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [next, queue, media, policy] = await Promise.all([
        apiRequest<Overview>("/api/admin/overview"),
        apiRequest<{ reports: Report[] }>("/api/admin/reports"),
        apiRequest<{ sfu: SfuStatus }>("/api/admin/sfu"),
        loadRegistrationPolicy(),
      ]);
      setOverview(next);
      setReports(queue.reports);
      setSfu(media.sfu);
      setRegistrationMode(policy);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось загрузить состояние");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const changeRegistrationMode = async (mode: RegistrationMode) => {
    if (mode === registrationMode) return;
    setBusyId("registration-policy");
    setError(null);
    setStatus(null);
    try {
      const result = await apiRequest<{ mode: RegistrationMode }>("/api/admin/registration-policy", {
        method: "POST", body: { mode },
      });
      setRegistrationMode(result.mode);
      setStatus(result.mode === "open"
        ? "Открытая регистрация включена. Пользователи могут создавать аккаунты самостоятельно."
        : "Регистрация закрыта. Новые аккаунты может создавать только администратор.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось изменить режим регистрации");
    } finally {
      setBusyId(null);
    }
  };

  const createUser = async (event: FormEvent) => {
    event.preventDefault();
    setBusyId("create-user");
    setError(null);
    setStatus(null);
    try {
      const result = await apiRequest<{ user: { id: string; username: string; displayName: string } }>("/api/admin/users", {
        method: "POST", body: { username, displayName, password },
      });
      setUsername("");
      setDisplayName("");
      setPassword("");
      setStatus(`Аккаунт @${result.user.username} создан. Пользователь может войти с заданным вами паролем.`);
      setOverview((current) => current ? { ...current, counts: { ...current.counts, users: (current.counts.users ?? 0) + 1 } } : current);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось создать аккаунт");
    } finally {
      setBusyId(null);
    }
  };

  const decide = async (report: Report, state: "resolved" | "dismissed") => {
    if (state === "resolved" && !window.confirm("Подтвердить жалобу и удалить сообщение у всех участников?")) return;
    setBusyId(report.id);
    setError(null);
    setStatus(null);
    try {
      await apiRequest<{ effect: "dismissed" | "message_removed" }>(`/api/admin/reports/${report.id}/decision`, { method: "POST", body: { state } });
      setReports((current) => current.filter((item) => item.id !== report.id));
      setStatus(state === "resolved" ? "Жалоба подтверждена: сообщение удалено у всех участников." : "Жалоба отклонена без изменения сообщения.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось применить решение");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation">
      <section aria-labelledby="admin-title" aria-modal="true" className="modal-card admin-modal" role="dialog">
        <header><div><p className="eyebrow">АДМИНИСТРИРОВАНИЕ</p><h2 id="admin-title">Управление личным кабинетом</h2></div><button aria-label="Закрыть администрирование" className="icon-button" onClick={onClose} type="button"><X /></button></header>
        {error && <div className="form-error" role="alert">{error}</div>}
        {status && <div className="form-success" role="status"><Check />{status}</div>}

        <section aria-labelledby="registration-title" className="admin-access">
          <div><h3 id="registration-title">Регистрация пользователей</h3><p>Режим сохраняется на сервере и действует для сайта и приложения.</p></div>
          <div aria-label="Режим регистрации" className="segmented" role="group">
            <button aria-pressed={registrationMode === "open"} className={registrationMode === "open" ? "active" : ""} disabled={loading || Boolean(busyId) || registrationMode === null} onClick={() => void changeRegistrationMode("open")} type="button">Открытая регистрация</button>
            <button aria-pressed={registrationMode === "closed"} className={registrationMode === "closed" ? "active" : ""} disabled={loading || Boolean(busyId) || registrationMode === null} onClick={() => void changeRegistrationMode("closed")} type="button">Только администратор</button>
          </div>
          <small>{registrationMode === null ? "Загружаем режим регистрации…" : registrationMode === "open" ? "Любой посетитель может зарегистрироваться самостоятельно." : "Самостоятельная регистрация отключена. Создайте аккаунт в форме ниже."}</small>
          <form className="admin-user-form" onSubmit={(event) => void createUser(event)}>
            <h4><UserPlus /> Создать аккаунт пользователя</h4>
            <label><span>Имя пользователя</span><input autoCapitalize="none" autoComplete="off" maxLength={32} minLength={3} onChange={(event) => setUsername(event.target.value)} pattern="[A-Za-z0-9_]{3,32}" placeholder="username" required value={username} /></label>
            <label><span>Отображаемое имя</span><input autoComplete="off" maxLength={80} onChange={(event) => setDisplayName(event.target.value)} placeholder="Имя участника" required value={displayName} /></label>
            <label><span>Пароль пользователя</span><input autoComplete="new-password" maxLength={256} minLength={12} onChange={(event) => setPassword(event.target.value)} placeholder="Не менее 12 символов" required type="password" value={password} /></label>
            <p>Передайте пользователю логин и заданный пароль. Его устройство подключится при первом входе. Ваша текущая сессия не изменится.</p>
            <button className="primary-button compact" disabled={loading || Boolean(busyId)} type="submit"><UserPlus />{busyId === "create-user" ? "Создаём…" : "Создать пользователя"}</button>
          </form>
        </section>

        {overview && <>
          <div className="admin-status"><span><Server /><b>API v{overview.version}</b><small>{new Date(overview.timestamp).toLocaleString("ru-RU")}</small></span><span><Activity /><b>{overview.websocketConnections} WebSocket</b><small>сейчас подключено</small></span><span><Database /><b>{overview.storageBackend}</b><small>Push: {overview.pushEnabled ? "включён" : "выключен"}</small></span></div>
          <div className="admin-counts">{Object.entries(overview.counts).map(([name, value]) => <span key={name}><b>{value.toLocaleString("ru-RU")}</b><small>{name}</small></span>)}</div>
          {sfu && <div className="admin-sfu"><span><Activity /><span><b>SFU: {sfu.enabled ? "работает" : "выключен"}</b><small>{sfu.workers} медиаворкеров</small></span></span><span><b>{sfu.rooms}</b><small>комнат</small></span><span><b>{sfu.participants}</b><small>участников</small></span><span><b>{sfu.producers}</b><small>исходящих потоков</small></span><span><b>{sfu.consumers}</b><small>получателей</small></span></div>}
          <p className="admin-privacy">Здесь отображаются агрегированные показатели работы сервиса.</p>
        </>}
        <div className="manager-section-head"><span>Жалобы <b>{reports.length}</b></span><button aria-label="Обновить состояние" className="icon-button" disabled={loading || Boolean(busyId)} onClick={() => void load()} type="button"><RefreshCw className={loading ? "spin-icon" : ""} /></button></div>
        <div className="admin-reports">{reports.length === 0 && <p className="manager-empty">Открытых жалоб нет.</p>}{reports.map((report) => <article key={report.id}><span><ShieldAlert /><b>{report.category}</b><small>Пожаловался @{report.reporterUsername} · на @{report.reportedUsername ?? "удалённого пользователя"} · {new Date(report.createdAt).toLocaleString("ru-RU")}</small><small>{report.chatName ?? "Чат"} · {report.messageRetracted ? "сообщение уже удалено" : report.messageExists ? "сообщение существует" : "сообщение недоступно"}</small></span><blockquote>{report.disclosedContent}</blockquote>{report.comment && <p>{report.comment}</p>}<footer><button disabled={Boolean(busyId)} onClick={() => void decide(report, "dismissed")} type="button"><X />Отклонить без действий</button><button disabled={Boolean(busyId) || !report.messageExists} onClick={() => void decide(report, "resolved")} type="button"><Check />Подтвердить и удалить сообщение</button></footer></article>)}</div>
      </section>
    </div>
  );
}
